import type { Booking } from "@/lib/bookings";
import type { Coupon, Payment } from "@/lib/adminCommerce";
import type { Visitor } from "@/lib/adminModules";

export interface MonthRevenue {
  key: string;
  label: string;
  collected: number;
  pending: number;
  refunded: number;
  net: number;
  transactions: number;
}

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function monthKey(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}`;
}

/** Rolls payments up into a monthly collected / pending / refunded / net series. */
export function aggregatePaymentsByMonth(payments: Payment[], months = 12): MonthRevenue[] {
  const buckets = new Map<string, MonthRevenue>();
  const now = new Date();

  for (let index = months - 1; index >= 0; index -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    const key = `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}`;
    buckets.set(key, {
      key,
      label: `${MONTH_LABELS[date.getMonth()]} ${`${date.getFullYear()}`.slice(2)}`,
      collected: 0,
      pending: 0,
      refunded: 0,
      net: 0,
      transactions: 0,
    });
  }

  payments.forEach((payment) => {
    const key = monthKey(payment.paid_at || payment.created_at);
    if (!key) return;
    const bucket = buckets.get(key);
    if (!bucket) return;
    const amount = Number(payment.amount || 0);
    bucket.transactions += 1;
    if (payment.status === "paid") bucket.collected += amount;
    else if (payment.status === "pending") bucket.pending += amount;
    else if (payment.status === "refunded") bucket.refunded += amount;
    bucket.net = bucket.collected - bucket.refunded;
  });

  return Array.from(buckets.values());
}

export interface BookingSummary {
  total: number;
  confirmed: number;
  pending: number;
  cancelled: number;
  completed: number;
  guests: number;
  upcoming: number;
}

export function summarizeBookings(bookings: Booking[]): BookingSummary {
  const today = new Date();
  const iso = `${today.getFullYear()}-${`${today.getMonth() + 1}`.padStart(2, "0")}-${`${today.getDate()}`.padStart(2, "0")}`;
  return bookings.reduce<BookingSummary>(
    (acc, booking) => {
      acc.total += 1;
      acc.guests += Number(booking.group_size || 0);
      if (booking.status === "confirmed") acc.confirmed += 1;
      else if (booking.status === "pending") acc.pending += 1;
      else if (booking.status === "cancelled") acc.cancelled += 1;
      else if (booking.status === "completed") acc.completed += 1;
      if ((booking.preferred_date || "") >= iso && booking.status !== "cancelled") acc.upcoming += 1;
      return acc;
    },
    { total: 0, confirmed: 0, pending: 0, cancelled: 0, completed: 0, guests: 0, upcoming: 0 },
  );
}

export function groupBookingsByType(bookings: Booking[]): { label: string; value: number }[] {
  const map = new Map<string, number>();
  bookings.forEach((booking) => {
    const key = booking.booking_type || "general";
    map.set(key, (map.get(key) || 0) + 1);
  });
  return Array.from(map.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
}

export function groupBookingsBySource(bookings: Booking[]): { label: string; value: number }[] {
  const map = new Map<string, number>();
  bookings.forEach((booking) => {
    const key = booking.source_page || "direct";
    map.set(key, (map.get(key) || 0) + 1);
  });
  return Array.from(map.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
}

/* ------------------------------ Customer Reports ---------------------------- */
export interface CustomerReportRow {
  id: number;
  name: string;
  email: string;
  phone: string;
  visitor_type: string;
  location: string;
  bookings: number;
  visits: number;
  spend: number;
  last_visit: string | null;
  status: string;
}

export function buildCustomerReportRows(visitors: Visitor[]): CustomerReportRow[] {
  return visitors
    .map((visitor) => ({
      id: visitor.id,
      name: visitor.name,
      email: visitor.email || "",
      phone: visitor.phone || "",
      visitor_type: visitor.visitor_type,
      location: visitor.location || "",
      bookings: Number(visitor.total_bookings || 0),
      visits: Number(visitor.total_visits || 0),
      spend: Number(visitor.total_spend || 0),
      last_visit: visitor.last_visit,
      status: visitor.status,
    }))
    .sort((a, b) => b.spend - a.spend);
}

export interface CustomerSummary {
  total: number;
  repeat: number;
  totalSpend: number;
  avgSpend: number;
  blocked: number;
  totalBookings: number;
}

export function summarizeCustomers(rows: CustomerReportRow[]): CustomerSummary {
  const totalSpend = rows.reduce((sum, row) => sum + row.spend, 0);
  const totalBookings = rows.reduce((sum, row) => sum + row.bookings, 0);
  return {
    total: rows.length,
    repeat: rows.filter((row) => row.visits > 1 || row.bookings > 1).length,
    totalSpend,
    avgSpend: rows.length ? Math.round(totalSpend / rows.length) : 0,
    blocked: rows.filter((row) => row.status === "blocked").length,
    totalBookings,
  };
}

/* ----------------------------- Marketing Reports ----------------------------- */
export interface ChannelRow {
  id: number;
  label: string;
  bookings: number;
  guests: number;
  share: number;
}

export function buildMarketingChannels(bookings: Booking[]): ChannelRow[] {
  const map = new Map<string, { bookings: number; guests: number }>();
  bookings.forEach((booking) => {
    const key = (booking.source_page || "direct").trim() || "direct";
    const entry = map.get(key) || { bookings: 0, guests: 0 };
    entry.bookings += 1;
    entry.guests += Number(booking.group_size || 0);
    map.set(key, entry);
  });
  const total = bookings.length || 1;
  return Array.from(map.entries())
    .map(([label, value], index) => ({
      id: index + 1,
      label,
      bookings: value.bookings,
      guests: value.guests,
      share: Math.round((value.bookings / total) * 100),
    }))
    .sort((a, b) => b.bookings - a.bookings);
}

export interface CouponPerfRow {
  id: number;
  code: string;
  discount: string;
  used: number;
  limit: number;
  redemption: number;
  status: string;
}

export function buildCouponPerformance(coupons: Coupon[]): CouponPerfRow[] {
  return coupons
    .map((coupon, index) => ({
      id: index + 1,
      code: coupon.code,
      discount:
        coupon.discount_type === "percent" ? `${coupon.discount_value}%` : `₹${coupon.discount_value}`,
      used: Number(coupon.used_count || 0),
      limit: Number(coupon.usage_limit || 0),
      redemption: coupon.usage_limit
        ? Math.round((Number(coupon.used_count || 0) / Number(coupon.usage_limit)) * 100)
        : 0,
      status: coupon.status,
    }))
    .sort((a, b) => b.used - a.used);
}